import { useFormik } from "formik";
import * as Yup from "yup";
import { InputText } from "@/shared/components/form/InputText";
import { Button } from "@/shared/components/form/Button";
import useTranslations from "@/shared/lang/useTranslations";
import { useEffect, useMemo } from "react";
import { AlertCircle, Lock } from "lucide-react";

interface ChangePasswordPageProps {
  isLoading?: boolean;
  onSubmit: (values: { currentPassword: string; newPassword: string }) => void;
  error?: string;
  onBack?: () => void;
}

export const ChangePasswordPage = ({
  isLoading = false,
  onSubmit,
  error,
  onBack,
}: ChangePasswordPageProps) => {
  const { t } = useTranslations("login");
  const { t: tg } = useTranslations("global");

  const validationSchema = useMemo(
    () =>
      Yup.object({
        currentPassword: Yup.string().required(tg("input_error_required")),
        newPassword: Yup.string()
          .min(6, tg("input_error_min_length", { length: 6 }))
          .required(tg("input_error_required")),
      }),
    [tg, t]
  );

  const {
    handleSubmit,
    getFieldProps,
    errors,
    touched,
    isValid,
    validateForm,
  } = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: {
      currentPassword: "",
      newPassword: "",
    },
    onSubmit: (values) => {
      onSubmit({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
    },
    validationSchema,
  });

  useEffect(() => {
    validateForm();
  }, [validateForm]);

  return (
    <div className="flex flex-col items-center w-full max-w-md mx-auto">
      {/* Header Section */}
      <div className="flex flex-col items-center gap-4 mb-8 animate-fade-down">
        <div className="relative">
          <div className="absolute inset-0 bg-green-500/20 dark:bg-green-500/20 rounded-full blur-xl animate-pulse"></div>
          <div className="relative bg-green-500/10 p-2 rounded-full dark:bg-green-500/20">
            <Lock className="w-8 h-8 text-green-500 dark:text-green-400" />
          </div>
        </div>

        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-foreground">
            {t("change_password_title")}
          </h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            {t("change_password_subtitle")}
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-5 animate-fade-up w-full items-center"
      >
        <InputText
          label={t("current_password_label")}
          isPassword
          placeholder={t("current_password_placeholder")}
          showPasswordLabel={tg("input_show_password_label")}
          hidePasswordLabel={tg("input_hide_password_label")}
          autoComplete="current-password"
          errorMessage={touched.currentPassword && errors.currentPassword}
          {...getFieldProps("currentPassword")}
        />
        <InputText
          label={t("new_password_label")}
          isPassword
          placeholder={t("new_password_placeholder")}
          showPasswordLabel={tg("input_show_password_label")}
          hidePasswordLabel={tg("input_hide_password_label")}
          autoComplete="new-password"
          errorMessage={touched.newPassword && errors.newPassword}
          {...getFieldProps("newPassword")}
        />

        {error && (
          <div className="flex items-center w-full gap-2 px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg animate-shake">
            <AlertCircle className="w-4 h-4 text-destructive shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <Button
          className="text-lg font-semibold h-12 w-full"
          type="submit"
          disabled={!isValid || isLoading}
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
              {tg("loading_button")}
            </span>
          ) : (
            t("change_password_button")
          )}
        </Button>

        {onBack && (
          <div className="text-center pt-4">
            <button
              type="button"
              onClick={onBack}
              disabled={isLoading}
              className="text-xs font-semibold cursor-pointer relative inline-block group transition-all duration-300 hover:text-green-500 py-1 dark:hover:text-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("back_button")}
              <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-green-400 dark:bg-green-300 rounded-full transition-all duration-300 group-hover:w-full"></span>
            </button>
          </div>
        )}
      </form>
    </div>
  );
};
